/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { artworkSchedulesAPI } from '../utils/api';
import { toast } from 'react-toastify';

// Per-customer artwork schedule view. After the flatten refactor each booking
// has exactly one ArtworkSchedule, so the page shows N rows where N is the
// number of flat bookings this customer has.
const ArtworkSchedule = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); // [{ booking, schedule }]
  const [customer, setCustomer] = useState(null);
  const [availableArtworks, setAvailableArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchCustomerSchedules();
  }, [customerId]);

  const fetchCustomerSchedules = async () => {
    try {
      setLoading(true);
      const response = await artworkSchedulesAPI.forCustomer(customerId);
      const data = response.data?.data || [];
      setRows(data);

      // Derive the customer from the first booking's populated customer field
      const firstCustomer = data[0]?.schedule?.customer || data[0]?.booking?.customer;
      setCustomer(firstCustomer && typeof firstCustomer === 'object' ? firstCustomer : null);

      // Available artworks for this customer
      if (firstCustomer?._id || customerId) {
        const cid = firstCustomer?._id || customerId;
        try {
          const artworksResponse = await api.get(`/artworks/customer/${cid}`);
          setAvailableArtworks(artworksResponse.data.data || []);
        } catch (artErr) {
          setAvailableArtworks([]);
        }
      }
    } catch (error) {
      console.error('Error fetching artwork schedules:', error);
      toast.error('Failed to load artwork schedules');
    } finally {
      setLoading(false);
    }
  };

  const updateSchedule = async (bookingId, payload) => {
    setUpdating(true);
    try {
      await artworkSchedulesAPI.updateForBooking(bookingId, payload);
      await fetchCustomerSchedules();
      toast.success('Schedule updated');
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update schedule');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  if (rows.length === 0) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p className="text-gray-500">No artwork schedules found for this customer.</p>
        </div>
      </Layout>
    );
  }

  const tbcRows = rows.filter(r => r.schedule?.scheduleStatus === 'TBC');

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/bookings')}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back to Bookings
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Artwork Schedule</h1>
          <p className="text-gray-600 mt-2">
            Customer: {customer?.name || 'Unknown'}
            {customer?.companyName ? ` (${customer.companyName})` : ''}
          </p>
        </div>

        {/* Schedule Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Schedules</h2>
            <p className="text-sm text-gray-600 mt-1">
              One row per booking. Set the artwork to mark a row as Confirmed.
            </p>
          </div>

          <div className="overflow-x-auto overflow-y-visible">
            <table className="min-w-full divide-y divide-gray-200 table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Magazine</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Finish</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Artwork</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map(({ booking, schedule }) => {
                  const status = schedule?.scheduleStatus || 'TBC';
                  const rowColor =
                    status === 'Confirmed'
                      ? 'bg-green-50'
                      : status === 'Pending'
                        ? 'bg-blue-50'
                        : status === 'Cancelled'
                          ? 'bg-gray-100'
                          : 'bg-red-50';

                  return (
                    <tr key={booking._id} className={`hover:bg-opacity-80 ${rowColor}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {booking.magazine?.name || 'Unknown Magazine'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {booking.contentSize?.adSize?.name || booking.contentSize?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {booking.contentType || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {booking.startIssue || schedule?.startIssue || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {booking.isOngoing
                          ? 'Ongoing'
                          : booking.finishIssue || schedule?.finishIssue || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            status === 'Confirmed'
                              ? 'bg-green-200 text-green-800'
                              : status === 'TBC'
                                ? 'bg-red-200 text-red-800'
                                : status === 'Pending'
                                  ? 'bg-blue-200 text-blue-800'
                                  : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={schedule?.artwork?._id || ''}
                          onChange={e =>
                            updateSchedule(booking._id, { artworkId: e.target.value || null })
                          }
                          disabled={updating}
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="">TBC</option>
                          {availableArtworks.map(art => (
                            <option key={art._id} value={art._id}>
                              {art.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          defaultValue={schedule?.entryNotes || ''}
                          onBlur={e => {
                            if (e.target.value !== (schedule?.entryNotes || '')) {
                              updateSchedule(booking._id, { entryNotes: e.target.value });
                            }
                          }}
                          placeholder="Notes…"
                          className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                          disabled={updating}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* TBC Summary */}
        {tbcRows.length > 0 && (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">
              To Be Confirmed (TBC) Items
            </h3>
            <p className="text-yellow-700 text-sm mb-3">
              The following bookings still need artwork confirmation:
            </p>
            <ul className="list-disc list-inside text-yellow-700 text-sm">
              {tbcRows.map(({ booking }) => (
                <li key={booking._id}>
                  {booking.magazine?.name}: {booking.startIssue}
                  {booking.isOngoing
                    ? ' – Ongoing'
                    : booking.finishIssue
                      ? ` – ${booking.finishIssue}`
                      : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ArtworkSchedule;
